import { Table } from '@radix-ui/themes'
import { NavigateFn, useNavigate } from '@tanstack/react-router'
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  Row,
  RowData,
  RowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { normalizeString } from 'components/table/enhancements/filters/filterUtils'
import { Pagination } from 'components/table/enhancements/Pagination'
import { TableTopBar } from 'components/table/enhancements/TableTopBar'
import { TableCheckbox } from 'components/TableCheckbox'
import { compareAsc, parseISO } from 'date-fns'
import isEqual from 'lodash/isEqual'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { FormField, InputType } from 'types'

import { HeaderCell } from './cells/HeaderCell'
import { BulkActionModal } from './enhancements/BulkEditModal'
import {
  getTableColumnHeaderId,
  getTableColumnHeaderLabelId,
} from './tableA11y'
import { TableBodyRow } from './TableBodyRow'
import { TableSkeletonRows } from './TableSkeletonRows'
import {
  StyledPaginationContainer,
  StyledSubheaderSection,
  StyledTableBody,
  StyledTableCell,
  StyledTableHeader,
  StyledTableRoot,
  StyledTableRootWithMaxHeight,
  StyledTableRow,
  TableContainer,
  TopBarContainer,
} from './TableStyles'

declare module '@tanstack/react-table' {
  // Typescript is complaining that TData and TValue are unused, but they are necessary in the ColumnMeta interface
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    inputType: InputType
  }

  interface FilterFns {
    customGlobalFilter: FilterFn<unknown>
    dateRangeFilter: FilterFn<unknown>
    multiSelectFilter: FilterFn<unknown>
  }
}

type ColumnDefWithAccessor<TData> = ColumnDef<TData> & { accessorKey?: string }

type ExpandableProps<TData extends object> =
  | {
      enableExpanding: true
      getRowCanExpand: (row: Row<TData>) => boolean
      getSubRows: (row: TData) => TData[] | undefined
      filterFromLeafRows: boolean
    }
  | {
      enableExpanding?: false
      getRowCanExpand?: never
      getSubRows?: never
      filterFromLeafRows?: never
    }

export type TableComponentProps<TData extends object> = {
  data: TData[]
  isLoadingData: boolean
  columns: ColumnDef<TData>[]
  itemDescriptiveName: string
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>
  onRowClick?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  onRowAuxClick?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  onRowContextMenu?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  onRowKeyDown?: (params: {
    event: React.KeyboardEvent<HTMLTableRowElement>
    row: Row<TData>
    rowElement: HTMLTableRowElement
  }) => void
  onRowFocus?: (row: Row<TData>) => void
  showFilterBar?: boolean
  globalFilterFn?: FilterFn<TData>
  leadingContent?: React.ReactNode
  subHeaderContent?: React.ReactNode
  customTopBar?: (props: {
    columns: ColumnDef<TData>[]
    setGlobalFilter: React.Dispatch<unknown>
    getColumn: (columnID: string) => Column<TData, unknown> | undefined
    initialColumnFilters?: ColumnFiltersState
    navigate: NavigateFn
    disableUrlFilters?: boolean
  }) => React.ReactNode
  onFilteredDataChange?: (
    filteredData: RowModel<TData>,
    globalFilter: unknown,
    columnFilters: ColumnFiltersState
  ) => void
  showTable?: boolean
  bulkEditEnabled?: boolean
  onBulkEdit?: (
    selectedRows: Row<TData>[],
    fieldKey: string,
    newValue: string | number | string[] | undefined
  ) => void
  onBulkDelete?: (selectedRows: Row<TData>[]) => void
  customBulkActions?: Array<{
    key: string
    label: string
    component?: React.ComponentType<{
      selectedRows: Row<TData>[]
      onClose: () => void
    }>
    buttonProps?: { danger?: boolean; ghost?: boolean }
  }>
  editableFields?: {
    label: string
    value: string | FormField | ColumnDef<TData>
  }[]
  showColumnConfig?: boolean
  showFooter?: boolean
  initialColumnFilters?: ColumnFiltersState
  urlFilters?: Record<
    string,
    string | number | boolean | string[] | [string | null, string | null]
  >
  disableUrlFilters?: boolean
  defaultVisibleColumnIds?: string[]
  isTableHeaderSticky?: boolean
  // If provided, render the TableTopBar into this external container via portal
  externalTopBarContainer?: HTMLElement | null
  // Row selection props
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean)
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  getRowId?: (originalRow: TData, index: number) => string
  customRowRenderer?: (row: Row<TData>, index: number) => React.ReactNode
  customSubComponentRenderer?: (props: {
    row: Row<TData>
  }) => React.ReactElement
  getRowHtmlAttributes?: (
    row: Row<TData>
  ) => React.HTMLAttributes<HTMLTableRowElement>
  maxHeight?: string
  cellPadding?: string
  cellFontSize?: string
  borderSpacing?: string
  highlightSelectedRow?: boolean
  // Controlled highlighted row ID - when provided, overrides internal state
  controlledHighlightedRowId?: string | null
  // Hover callbacks for 2-way hover synchronization
  onRowHoverStart?: (row: Row<TData>) => void
  onRowHoverEnd?: () => void
  // Controlled hovered row ID - when provided, applies hover styling
  controlledHoveredRowId?: string | null
  cardStyleRows?: boolean
  // Server-side pagination support
  manualPagination?: boolean
  totalRowCount?: number
  manualFiltering?: boolean
  manualSorting?: boolean
} & ExpandableProps<TData>

export const TableComponent = <TData extends object>(
  props: TableComponentProps<TData>
) => {
  const navigate = useNavigate()

  const {
    isLoadingData,
    data,
    columns,
    itemDescriptiveName: footerName,
    onRowClick,
    onRowAuxClick,
    onRowContextMenu,
    onRowKeyDown,
    onRowFocus,
    pagination,
    setPagination,
    setSorting,
    sorting,
    showFilterBar = true,
    bulkEditEnabled = false,
    onBulkDelete,
    onBulkEdit,
    customBulkActions = [],
    editableFields = [],
    globalFilterFn,
    leadingContent,
    onFilteredDataChange,
    showTable = true,
    showColumnConfig = true,
    showFooter = true,
    initialColumnFilters = [],
    urlFilters = {},
    disableUrlFilters = false,
    defaultVisibleColumnIds,
    isTableHeaderSticky = true,
    customTopBar,
    // Expandable props
    enableExpanding = false,
    getRowCanExpand,
    getSubRows,
    filterFromLeafRows,
    // Row selection props
    enableRowSelection = false,
    rowSelection = {},
    onRowSelectionChange,
    getRowId,
    // Custom row renderer
    customRowRenderer,
    // Custom sub component renderer (used for expandable rows when more complex rendering is needed)
    // Use getSubRows if expanded rows match parent format
    customSubComponentRenderer,
    getRowHtmlAttributes,
    maxHeight,
    cellPadding,
    cellFontSize,
    borderSpacing,
    highlightSelectedRow = false,
    controlledHighlightedRowId,
    onRowHoverStart,
    onRowHoverEnd,
    controlledHoveredRowId,
    cardStyleRows = true,
    manualPagination = false,
    totalRowCount,
    manualFiltering = false,
    manualSorting = false,
  } = props

  const [internalHighlightedRowId, setInternalHighlightedRowId] = useState<
    string | null
  >(null)

  const highlightedRowId =
    controlledHighlightedRowId ?? internalHighlightedRowId

  const [globalFilter, setGlobalFilter] = useState<unknown>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<{
    [key: string]: boolean
  }>({})
  const [expandedState, setExpandedState] = useState<ExpandedState>({})
  const hasInitialized = React.useRef(false)
  const previousUrlFilters = React.useRef(urlFilters)
  const prevFiltersRef = React.useRef<ColumnFiltersState>(columnFilters)
  const prevGlobalFilterRef = React.useRef(globalFilter)
  const [showBulkActionModal, setShowBulkActionModal] = useState<boolean>(false)
  const tableId = React.useId()

  // Convert URL filters to column filters state
  const initialFilters = React.useMemo(() => {
    return Object.entries(urlFilters).map(([id, value]) => ({
      id,
      value,
    }))
  }, [urlFilters])

  const defaultFilterFn: FilterFn<TData> = () => true

  const dateRangeFilterFn: FilterFn<TData> = (row, columnId, filterValue) => {
    const [startDateStr, endDateStr] = filterValue
    const rowValue = row.getValue(columnId)

    // Return true if no filter is applied
    if (!rowValue || (!startDateStr && !endDateStr)) return true

    try {
      // Handle both Date objects and string values
      let rowDate: Date
      if (rowValue instanceof Date) {
        rowDate = rowValue
      } else {
        const rowDateStr = String(rowValue)
        rowDate = parseISO(rowDateStr)
        if (isNaN(rowDate.getTime())) return true
      }

      // Parse filter dates only if they exist
      const startDate = startDateStr ? parseISO(startDateStr) : null
      const endDate = endDateStr ? parseISO(endDateStr) : null

      // Validate parsed dates
      if (startDate && isNaN(startDate.getTime())) return true
      if (endDate && isNaN(endDate.getTime())) return true

      // Set end date to end of day for inclusive comparison
      if (endDate) {
        endDate.setHours(23, 59, 59, 999)
      }

      const isAfterStart = startDate
        ? compareAsc(rowDate, startDate) >= 0
        : true
      const isBeforeEnd = endDate ? compareAsc(rowDate, endDate) <= 0 : true

      return isAfterStart && isBeforeEnd
    } catch (error) {
      console.error('Error in dateRangeFilterFn:', error)
      return true
    }
  }

  const multiSelectFilterFn: FilterFn<TData> = (row, columnId, filterValue) => {
    const rowValue = row.getValue(columnId)
    const normalizedFilterValues = filterValue.map((v: string) =>
      normalizeString(v)
    )

    const processValue = (value: string) => {
      const normalizedValue = normalizeString(value)
      return normalizedFilterValues.includes(normalizedValue)
    }

    if (typeof rowValue === 'string') {
      return processValue(rowValue)
    } else if (Array.isArray(rowValue)) {
      return rowValue.some(
        (item) => typeof item === 'string' && processValue(item)
      )
    }

    return false
  }

  // Will get passed to the top bar component to check if we should have the button clickable
  const onClickBulkEditButton = () => {
    const selectedRows = table.getSelectedRowModel()
    if (selectedRows.rows.length > 0) {
      setShowBulkActionModal(true)
    }
  }

  const handleRowClick = (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (highlightSelectedRow && controlledHighlightedRowId === undefined) {
      setInternalHighlightedRowId(
        row.id === internalHighlightedRowId ? null : row.id
      )
    }
    onRowClick?.(row, event)
  }

  const handleRowFocus = (row: Row<TData>) => {
    if (highlightSelectedRow && controlledHighlightedRowId === undefined) {
      setInternalHighlightedRowId(row.id)
    }
    onRowFocus?.(row)
  }

  // selection column if row selection is enabled
  const selectionColumns = useMemo((): ColumnDef<TData>[] => {
    if (!enableRowSelection) {
      return []
    }

    const selectionColumn: ColumnDef<TData> = {
      id: 'select',
      header: ({ table }) => {
        return (
          <TableCheckbox
            checked={table.getIsAllRowsSelected()}
            ref={(el) => {
              if (el) el.indeterminate = table.getIsSomeRowsSelected()
            }}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        )
      },
      cell: ({ row }) => (
        <TableCheckbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      size: 40,
      meta: {
        inputType: 'checkbox' as InputType,
      },
    }

    return [selectionColumn]
  }, [enableRowSelection])

  // combine selection columns and columns that were passed in
  const allColumns = useMemo(
    () => [...selectionColumns, ...columns],
    [selectionColumns, columns]
  )

  const tableConfig = useMemo(
    () => ({
      data,
      autoResetPageIndex: false,
      columns: allColumns,
      filterFns: {
        customGlobalFilter: globalFilterFn ?? defaultFilterFn,
        dateRangeFilter: dateRangeFilterFn,
        multiSelectFilter: multiSelectFilterFn,
      },
      getCoreRowModel: getCoreRowModel<TData>(),
      getFacetedRowModel: getFacetedRowModel<TData>(),
      ...(!manualFiltering && {
        getFilteredRowModel: getFilteredRowModel<TData>(),
      }),
      ...(!manualSorting && {
        getSortedRowModel: getSortedRowModel<TData>(),
      }),
      onSortingChange: setSorting,
      ...(!manualPagination && {
        getPaginationRowModel: getPaginationRowModel<TData>(),
      }),
      ...(manualPagination && {
        manualPagination: true,
        pageCount:
          totalRowCount !== undefined
            ? Math.ceil(totalRowCount / pagination.pageSize)
            : -1,
      }),
      // Row selection configuration
      ...(enableRowSelection && {
        enableRowSelection,
        onRowSelectionChange,
      }),
      // Expandable configuration
      ...(enableExpanding && {
        getExpandedRowModel: getExpandedRowModel<TData>(),
        getSubRows,
        getRowCanExpand,
        filterFromLeafRows,
        onExpandedChange: setExpandedState,
      }),
      // Important: when row selection is enabled
      // By default the selection change will track the array index of the row
      // But if the component parent wants `onRowSelectionChange` to take in specific ids for the row,
      // then `getRowId` must be provided to the table component
      ...(getRowId && { getRowId }),
      state: {
        globalFilter,
        columnFilters,
        pagination,
        sorting,
        columnVisibility,
        ...(enableRowSelection && { rowSelection }),
        ...(enableExpanding && { expanded: expandedState }),
      },
      onGlobalFilterChange: setGlobalFilter,
      globalFilterFn: globalFilterFn,
      onPaginationChange: setPagination,
      onColumnFiltersChange: (
        updaterOrValue:
          | ColumnFiltersState
          | ((old: ColumnFiltersState) => ColumnFiltersState)
      ) => {
        const newFilters =
          typeof updaterOrValue === 'function'
            ? updaterOrValue(columnFilters)
            : updaterOrValue
        setColumnFilters(newFilters)
        // Do not clear global filter when column filters are cleared
      },
      onColumnVisibilityChange: setColumnVisibility,
      getFacetedUniqueValues: getFacetedUniqueValues<TData>(),
      getFacetedMinMaxValues: getFacetedMinMaxValues<TData>(),
      meta: {
        tableId,
      },
    }),
    [
      data,
      allColumns,
      globalFilterFn,
      globalFilter,
      columnFilters,
      pagination,
      sorting,
      columnVisibility,
      setSorting,
      setPagination,
      setColumnVisibility,
      enableExpanding,
      getSubRows,
      getRowCanExpand,
      expandedState,
      setExpandedState,
      filterFromLeafRows,
      enableRowSelection,
      rowSelection,
      onRowSelectionChange,
      getRowId,
      manualPagination,
      manualFiltering,
      manualSorting,
      totalRowCount,
      tableId,
    ]
  )

  const table = useReactTable<TData>(tableConfig)

  // Initialize filters and column visibility only on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      if (defaultVisibleColumnIds && allColumns.length > 0) {
        const visibility: { [key: string]: boolean } = {}

        allColumns.forEach((col) => {
          const id = col.id
          const accessorKey = (col as ColumnDefWithAccessor<TData>).accessorKey
          const key = id || accessorKey
          if (typeof key === 'string') {
            visibility[key] = defaultVisibleColumnIds.includes(key)
          }
        })
        setColumnVisibility(visibility)
      }
      const filtersToApply =
        initialFilters.length > 0 ? initialFilters : initialColumnFilters || []
      if (filtersToApply.length > 0) {
        setColumnFilters(filtersToApply)
      }
      hasInitialized.current = true
    }
    // we need to do this to ensure that the filters are applied when the table is mounted
    // and not when the table is re-rendered
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter changes and notify parent
  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange(
        table.getSortedRowModel(),
        globalFilter,
        columnFilters
      )
    }
  }, [onFilteredDataChange, table, globalFilter, columnFilters, sorting])

  // Reset filters when URL filters are cleared
  useEffect(() => {
    // Only clear filters if URL filters were cleared by user action (not initially empty)
    const previousHadFilters =
      Object.keys(previousUrlFilters.current).length > 0
    const currentHasNoFilters = Object.keys(urlFilters).length === 0

    if (
      hasInitialized.current &&
      previousHadFilters &&
      currentHasNoFilters &&
      columnFilters.length > 0
    ) {
      setColumnFilters([])
      setGlobalFilter([])
    }

    // Update the previous URL filters reference
    previousUrlFilters.current = urlFilters

    // this is needed to ensure that the filters are reset when the url filters are cleared
    // and not when the table is re-rendered
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFilters])

  // Reset to first page when filters change
  useEffect(() => {
    const filtersChanged = !isEqual(prevFiltersRef.current, columnFilters)
    const globalFilterChanged = !isEqual(
      prevGlobalFilterRef.current,
      globalFilter
    )
    if (filtersChanged || globalFilterChanged) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
      prevFiltersRef.current = columnFilters
      prevGlobalFilterRef.current = globalFilter
    }
  }, [columnFilters, globalFilter, setPagination])

  const tableTopBarProps = {
    columns,
    setGlobalFilter,
    getColumn: table.getColumn,
    showFilterBar,
    showGlobalFilter: !!globalFilterFn,
    showColumnConfig,
    leadingContent,
    initialColumnFilters,
    navigate,
    disableUrlFilters,
    defaultVisibleColumnIds,
    bulkEditEnabled:
      bulkEditEnabled && table.getSelectedRowModel().rows.length > 0,
    onClickBulkEdit: onClickBulkEditButton,
    resultCount:
      manualPagination && totalRowCount !== undefined
        ? totalRowCount
        : table.getFilteredRowModel().rows.length,
  }

  const customTopBarProps = {
    columns,
    setGlobalFilter,
    getColumn: table.getColumn,
    initialColumnFilters,
    navigate,
    disableUrlFilters,
  }

  const tableTopBar = customTopBar ? (
    customTopBar(customTopBarProps)
  ) : (
    <TableTopBar {...tableTopBarProps} />
  )

  return (
    <>
      {(showFilterBar || globalFilterFn || leadingContent) &&
        (props.externalTopBarContainer ? (
          createPortal(tableTopBar, props.externalTopBarContainer)
        ) : (
          <TopBarContainer>{tableTopBar}</TopBarContainer>
        ))}

      {showTable && (
        <>
          {props.subHeaderContent && (
            <StyledSubheaderSection>
              {props.subHeaderContent}
            </StyledSubheaderSection>
          )}

          <TableContainer $maxHeight={maxHeight}>
            {(() => {
              const TableRootComponent = maxHeight
                ? StyledTableRootWithMaxHeight
                : StyledTableRoot

              return (
                <TableRootComponent
                  $cellPadding={cellPadding}
                  $cellFontSize={cellFontSize}
                  $borderSpacing={borderSpacing}>
                  <caption>
                    {/* eslint-disable-next-line no-classname/no-classname-outside-designsystem */}
                    <span className='sr-only'>{footerName}</span>
                    <span
                      className='sr-only' // eslint-disable-line no-classname/no-classname-outside-designsystem
                      role='status'
                      aria-live='polite'
                      aria-atomic='true'>{`${manualPagination && totalRowCount !== undefined ? totalRowCount : table.getFilteredRowModel().rows.length} results`}</span>
                  </caption>
                  <StyledTableHeader $isTableHeaderSticky={isTableHeaderSticky}>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <Table.Row key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          if (header.isPlaceholder) {
                            return (
                              <th
                                key={header.id}
                                colSpan={header.colSpan}
                                aria-hidden='true'
                                tabIndex={-1}>
                                {/* eslint-disable-next-line no-classname/no-classname-outside-designsystem */}
                                <span className='sr-only'>Placeholder</span>
                              </th>
                            )
                          }

                          const headerContent = header.column.columnDef.header
                          const isReactComponent =
                            typeof headerContent === 'function'

                          if (isReactComponent) {
                            return (
                              <Table.ColumnHeaderCell
                                key={header.id}
                                id={getTableColumnHeaderId(
                                  tableId,
                                  header.column.id
                                )}
                                scope='col'
                                tabIndex={0}>
                                <div style={{ padding: '0.75rem 0.25rem' }}>
                                  {flexRender(
                                    headerContent,
                                    header.getContext()
                                  )}
                                </div>
                              </Table.ColumnHeaderCell>
                            )
                          }

                          return (
                            <HeaderCell
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              canSort={header.column.getCanSort()}
                              isSorted={header.column.getIsSorted()}
                              columnId={header.id}
                              headerId={getTableColumnHeaderId(
                                tableId,
                                header.column.id
                              )}
                              headerLabelId={getTableColumnHeaderLabelId(
                                tableId,
                                header.column.id
                              )}
                              cellText={headerContent?.toString() || ''}
                            />
                          )
                        })}
                      </Table.Row>
                    ))}
                  </StyledTableHeader>
                  <StyledTableBody key={`page-${pagination.pageIndex}`}>
                    {isLoadingData ? (
                      <TableSkeletonRows
                        columnCount={allColumns.length}
                        cardStyleRows={cardStyleRows}
                      />
                    ) : table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row, index) => {
                        if (customRowRenderer) {
                          return customRowRenderer(row, index)
                        }

                        if (customSubComponentRenderer) {
                          return (
                            <Fragment key={row.id}>
                              <TableBodyRow
                                row={row}
                                enableExpanding={enableExpanding}
                                onRowClick={
                                  highlightSelectedRow || onRowClick
                                    ? handleRowClick
                                    : undefined
                                }
                                onRowAuxClick={onRowAuxClick}
                                onRowContextMenu={onRowContextMenu}
                                onRowKeyDown={onRowKeyDown}
                                onRowFocus={
                                  highlightSelectedRow || onRowFocus
                                    ? handleRowFocus
                                    : undefined
                                }
                                onRowHoverStart={onRowHoverStart}
                                onRowHoverEnd={onRowHoverEnd}
                                $isHighlighted={
                                  highlightSelectedRow &&
                                  highlightedRowId === row.id
                                }
                                $isHovered={controlledHoveredRowId === row.id}
                                cardStyleRows={cardStyleRows}
                                tableId={tableId}
                                htmlAttributes={getRowHtmlAttributes?.(row)}
                              />
                              {row.getIsExpanded() && (
                                <Table.Row>
                                  <Table.Cell
                                    colSpan={row.getVisibleCells().length}>
                                    {customSubComponentRenderer({ row })}
                                  </Table.Cell>
                                </Table.Row>
                              )}
                            </Fragment>
                          )
                        }

                        return (
                          <TableBodyRow
                            cardStyleRows={cardStyleRows}
                            key={row.id}
                            row={row}
                            enableExpanding={enableExpanding}
                            onRowClick={
                              highlightSelectedRow || onRowClick
                                ? handleRowClick
                                : undefined
                            }
                            onRowAuxClick={onRowAuxClick}
                            onRowContextMenu={onRowContextMenu}
                            onRowKeyDown={onRowKeyDown}
                            onRowFocus={
                              highlightSelectedRow ? handleRowFocus : undefined
                            }
                            onRowHoverStart={onRowHoverStart}
                            onRowHoverEnd={onRowHoverEnd}
                            $isHighlighted={
                              highlightSelectedRow &&
                              highlightedRowId === row.id
                            }
                            $isHovered={controlledHoveredRowId === row.id}
                            htmlAttributes={getRowHtmlAttributes?.(row)}
                            tableId={tableId}
                          />
                        )
                      })
                    ) : (
                      <StyledTableRow $hasOnClick={Boolean(onRowClick)}>
                        <StyledTableCell
                          colSpan={allColumns.length}
                          $cellPadding={cellPadding}
                          $cellFontSize={cellFontSize}>
                          No results.
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                  </StyledTableBody>
                </TableRootComponent>
              )
            })()}
          </TableContainer>
          <StyledPaginationContainer>
            {showFooter && (
              <footer>
                <div>{`${manualPagination && totalRowCount !== undefined ? totalRowCount : table.getFilteredRowModel().rows.length} ${footerName}`}</div>
                {enableRowSelection && (
                  <div>{`${table.getSelectedRowModel().rows.length} ${footerName} Selected`}</div>
                )}
              </footer>
            )}
            {(table.getPageCount() > 1 ||
              (manualPagination && table.getPageCount() === -1)) && (
              <Pagination
                getCanNextPage={table.getCanNextPage}
                getCanPreviousPage={table.getCanPreviousPage}
                tableNextPage={table.nextPage}
                tablePreviousPage={table.previousPage}
                tablePagesCount={table.getPageCount()}
                tableGetToPage={table.setPageIndex}
                tableCurrentPage={pagination.pageIndex}
              />
            )}
          </StyledPaginationContainer>
        </>
      )}
      {bulkEditEnabled && onBulkEdit && (
        <BulkActionModal
          isOpen={showBulkActionModal}
          onClose={() => setShowBulkActionModal(false)}
          selectedRows={table.getSelectedRowModel().rows}
          editableFields={editableFields}
          onBulkEdit={onBulkEdit}
          onBulkDelete={onBulkDelete}
          customActions={customBulkActions}
        />
      )}
    </>
  )
}
